/**
 * Users feature — entry point
 *
 * Structure:
 *   hooks/useUsers.js         — CRUD, toggle-active, save
 *   components/UserModal.jsx  — add/edit form modal
 */
import { useState } from 'react'
import { RiAddLine, RiDeleteBinLine, RiEditLine, RiSearchLine } from 'react-icons/ri'
import { Btn, Toggle, Badge, PageHeader, EmptyState, Pagination, FilterBar } from '../../components/ui/design.jsx'
import { useUsers } from './hooks/useUsers.js'
import UserModal from './components/UserModal.jsx'

const ROLE_LABEL = { super_admin: 'Super Admin', admin: 'Admin', editor: 'Editor' }
const ROLE_BADGE_CLS = {
  super_admin: 'bg-violet-100 text-violet-700',
  admin:       'bg-blue-100 text-blue-700',
  editor:      'bg-emerald-100 text-emerald-700',
}

export default function UsersPage() {
  const {
    me, users, loading, pagination, search, setSearch,
    load, remove, toggleActive, save,
  } = useUsers()

  const [editing, setEditing] = useState(null) // null | 'new' | user object

  const handleSave = async (form) => {
    await save(form)
    setEditing(null)
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Pengguna"
        description="Kelola akses masuk admin. Hanya Super Admin yang bisa membuat pengguna baru."
        action={
          <Btn onClick={() => setEditing('new')}>
            <RiAddLine /> Tambah Pengguna
          </Btn>
        }
      />

      <FilterBar>
        <div className="relative">
          <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400" />
          <input
            placeholder="Cari nama atau email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load(1)}
            className="h-9 w-56 rounded-lg border border-gray-200 bg-white pl-9 pr-3 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
          />
        </div>
      </FilterBar>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div
          className="hidden md:grid border-b border-gray-100 bg-gray-50/70 px-6 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500"
          style={{ gridTemplateColumns: '1fr 1.2fr 1fr auto auto' }}
        >
          <span>Nama</span>
          <span>Email</span>
          <span>Hak Akses</span>
          <span>Aktif</span>
          <span className="text-right">Aksi</span>
        </div>

        <div className="divide-y divide-gray-100">
          {loading && <EmptyState message="Memuat…" />}
          {!loading && users.map((u) => (
            <div
              key={u.id}
              className="flex flex-col gap-3 px-6 py-4 hover:bg-gray-50/50 transition-colors md:grid md:items-center md:gap-4"
              style={{ gridTemplateColumns: '1fr 1.2fr 1fr auto auto' }}
            >
              {/* Avatar + name */}
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-violet-500 text-xs font-bold text-white select-none">
                  {u.name?.[0]?.toUpperCase()}
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-900">
                    {u.name}
                    {u.id === me?.id && (
                      <span className="ml-1 text-xs font-normal text-gray-400">(Anda)</span>
                    )}
                  </div>
                </div>
              </div>

              {/* Email */}
              <div className="text-sm text-gray-500 truncate">{u.email}</div>

              {/* Roles */}
              <div className="flex flex-wrap gap-1">
                {u.roles.map((r) => (
                  <span
                    key={r}
                    className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${
                      ROLE_BADGE_CLS[r] || 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {ROLE_LABEL[r] || r}
                  </span>
                ))}
              </div>

              {/* Toggle */}
              <div>
                <Toggle
                  checked={u.is_active}
                  onChange={() => toggleActive(u)}
                  disabled={u.id === me?.id}
                  size="sm"
                />
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-1">
                <button
                  onClick={() => setEditing(u)}
                  className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                  title="Edit"
                >
                  <RiEditLine className="text-base" />
                </button>
                <button
                  onClick={() => remove(u)}
                  disabled={u.id === me?.id}
                  className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600 transition-colors disabled:opacity-30"
                  title="Hapus"
                >
                  <RiDeleteBinLine className="text-base" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Pagination pagination={pagination} onPage={load} />

      {editing && (
        <UserModal
          user={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSave={handleSave}
        />
      )}
    </div>
  )
}
